'use client';

import { c as _c } from "react/compiler-runtime";
import { getCaretCoordinates } from '@shared/utils/helpers/getCaretCoordinates';
import useTagSuggestion from '@shared/utils/hooks/useTagSuggestion';
import { useIsFetching } from '@tanstack/react-query';
import { Loader2, Search, X } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { decodeScope, encodeScope, useSearchScope } from '../../hooks/useSearchScope';
import { type PhotonSearchResult } from '../../services/photonSearch';
import { spaceConfig } from '../../utils/getSpaceConfig';
import TagSuggestionModal from '../posts/TagSuggestionModal';
import SearchDropdown from './SearchDropdown';
type Props = {
  className?: string;
};
const SearchBar = t0 => {
  const $ = _c(76);
  const {
    className
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const query = useSearchParams();
  const searchBarInputRef = useRef(null);
  let t1;
  if ($[0] !== query) {
    t1 = query?.get("q") ?? "";
    $[0] = query;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [content, setContent] = useState(t1);
  const [mounted, setMounted] = useState(false);
  const {
    scope: registeredScope,
    query: scopedQuery,
    dispatch
  } = useSearchScope();
  const isMapPage = pathname === "/map";
  const isSearchPage = pathname === "/search";
  let t2;
  if ($[2] !== query) {
    t2 = decodeScope(query?.get("scope") ?? null);
    $[2] = query;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const urlScope = t2;
  const scope = isSearchPage ? urlScope : registeredScope;
  const isBoardScope = scope?.kind === "board";
  const shouldNavigateToSearch = !isMapPage && !isSearchPage && !scope;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      queryKey: ["semanticSearch"]
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const semanticFetching = useIsFetching(t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      queryKey: ["boardSearch"]
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const boardFetching = useIsFetching(t4);
  const isSearching = (semanticFetching > 0 || boardFetching > 0) && (isSearchPage || Boolean(scope)) && mounted;
  const value = isBoardScope ? scopedQuery : content;
  const syncUrl = next => {
    const params = new URLSearchParams(isSearchPage ? window.location.search : "");
    const queryWords = extractQuery(next);
    if (queryWords.length > 0) {
      params.set("q", queryWords.join(" "));
    } else {
      params.delete("q");
    }
    const encoded = scope ? encodeScope(scope) : null;
    if (encoded) {
      params.set("scope", encoded);
    } else {
      params.delete("scope");
    }
    const url = `/search?${params.toString()}`;
    if (isSearchPage) {
      router.replace(url, {
        scroll: false
      });
    } else {
      router.push(url, {
        scroll: false
      });
    }
  };
  const setValue = next_0 => {
    if (isBoardScope) {
      dispatch({
        type: "query",
        query: next_0
      });
      return;
    }
    setContent(next_0);
    if (isSearchPage || scope) {
      syncUrl(next_0);
    }
  };
  let t5;
  if ($[6] !== dispatch || $[7] !== isSearchPage || $[8] !== router) {
    t5 = () => {
      if (isSearchPage) {
        const params_0 = new URLSearchParams(window.location.search);
        params_0.delete("scope");
        router.replace(`/search?${params_0.toString()}`, {
          scroll: false
        });
        return;
      }
      dispatch({
        type: "dismiss"
      });
    };
    $[6] = dispatch;
    $[7] = isSearchPage;
    $[8] = router;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const dismissScope = t5;
  let t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setMounted(true);
    };
    t7 = [];
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  const t8 = isMapPage || scope ? "" : content;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = async () => {
      const input = searchBarInputRef.current;
      if (!input) {
        return {
          top: 0,
          left: 0
        };
      }
      const coords = getCaretCoordinates(input);
      return {
        top: coords.top + coords.height,
        left: coords.left
      };
    };
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== router) {
    t10 = (type, _id, slug) => {
      const routeMap = {
        user: `/user/${slug}`,
        space: `/s/${slug}`,
        factiii: `/f/${slug}`
      } as const;
      router.push(routeMap[type]);
    };
    $[13] = router;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t8) {
    t11 = {
      content: t8,
      setContent,
      inputRef: searchBarInputRef,
      TagSuggestionModal,
      resetSearchBar: true,
      getCaretPosition: t9,
      onItemClick: t10
    };
    $[15] = t10;
    $[16] = t8;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const {
    renderSuggestions
  } = useTagSuggestion(t11);
  let t12;
  let t13;
  if ($[18] !== router || $[19] !== shouldNavigateToSearch) {
    t12 = () => {
      const handleKeyDown = function handleKeyDown(event) {
        if ((event.ctrlKey || event.metaKey) && event.key === "k") {
          event.preventDefault();
          if (shouldNavigateToSearch) {
            router.push("/search");
          } else {
            searchBarInputRef.current?.focus();
          }
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    };
    t13 = [shouldNavigateToSearch, router];
    $[18] = router;
    $[19] = shouldNavigateToSearch;
    $[20] = t12;
    $[21] = t13;
  } else {
    t12 = $[20];
    t13 = $[21];
  }
  useEffect(t12, t13);
  const searchHandler = e => {
    e.preventDefault();
    if (isBoardScope) {
      return;
    }
    if (!content) {
      return;
    }
    if (isMapPage) {
      return;
    }
    if (content.toLowerCase().includes("map") || content.toLowerCase().includes("location")) {
      router.push("/map");
      return;
    }
    return syncUrl(content);
  };
  let t14;
  if ($[22] !== router) {
    t14 = result => {
      const [longitude, latitude] = result.geometry.coordinates;
      setContent("");
      router.push(`/map?latitude=${latitude}&longitude=${longitude}`);
    };
    $[22] = router;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const handleSearchResultClick = t14;
  const extractQuery = _temp;
  let t15;
  if ($[24] !== isBoardScope || $[25] !== isMapPage || $[26] !== isSearchPage) {
    t15 = () => {
      if (isMapPage) {
        return "Search map";
      }
      if (isBoardScope) {
        return "Search cards";
      }
      if (isSearchPage) {
        return `Search ${spaceConfig.space.slug}...`;
      }
      return `Search ${spaceConfig.space.slug}`;
    };
    $[24] = isBoardScope;
    $[25] = isMapPage;
    $[26] = isSearchPage;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const getPlaceholder = t15;
  if (shouldNavigateToSearch) {
    const t16 = `relative flex h-11 w-full min-w-0 items-center overflow-hidden rounded-full border border-solid border-ds-border-standard bg-background-secondary px-2.5 py-0 align-middle no-underline transition-all duration-200 hover:border-ds-border-hover ${className || ""}`;
    let t17;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <div className="shrink-0 text-textSecondary transition-transform duration-200 hover:scale-110"><Search height={17} width={17} /></div>;
      $[28] = t17;
    } else {
      t17 = $[28];
    }
    const t18 = getPlaceholder();
    let t19;
    if ($[29] !== t18) {
      t19 = <div className="ml-2 flex w-full min-w-0 items-center justify-between">{t17}<span className="ml-2 w-0 grow truncate text-textSecondary">{t18}</span></div>;
      $[29] = t18;
      $[30] = t19;
    } else {
      t19 = $[30];
    }
    let t20;
    if ($[31] !== t16 || $[32] !== t19) {
      t20 = <Link href="/search" title="Search" className={t16}>{t19}</Link>;
      $[31] = t16;
      $[32] = t19;
      $[33] = t20;
    } else {
      t20 = $[33];
    }
    return t20;
  }
  const t16 = `relative flex h-11 w-full min-w-0 rounded-full border border-solid border-ds-border-standard bg-background-secondary px-2.5 py-0 align-middle transition-all duration-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 hover:border-ds-border-hover ${className || ""}`;
  const t17 = `transition-all duration-200 ${isSearching ? "scale-50 opacity-0" : "scale-100 opacity-100"}`;
  let t18;
  if ($[34] !== t17) {
    t18 = <Search height={17} width={17} className={t17} />;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const t19 = `absolute inset-0 animate-spin transition-all duration-200 ${isSearching ? "scale-100 opacity-100" : "scale-50 opacity-0"}`;
  let t20;
  if ($[36] !== t19) {
    t20 = <Loader2 height={17} width={17} className={t19} />;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t18 || $[39] !== t20) {
    t21 = <div className="relative shrink-0 text-textSecondary transition-transform duration-200 hover:scale-110">{t18}{t20}</div>;
    $[38] = t18;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== dismissScope || $[42] !== scope) {
    t22 = scope && <span data-id="search-scope-pill" className="ml-2 flex h-7 min-w-0 max-w-[50%] items-center gap-1 rounded-full bg-primary/15 py-0 pl-2.5 pr-1 text-sm font-emphasis text-primary ring-1 ring-inset ring-primary/25"><span className="min-w-0 truncate">{scope.label}</span><button type="button" onClick={dismissScope} aria-label={`Search all of ${spaceConfig.space.slug} instead of ${scope.label}`} title="Search the whole site" className="shrink-0 rounded-full p-0.5 text-primary/70 transition-colors hover:bg-primary/20 hover:text-primary"><X height={13} width={13} /></button></span>;
    $[41] = dismissScope;
    $[42] = scope;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== setValue || $[45] !== value) {
    t23 = e_0 => {
      if (e_0.key === "Escape" && value) {
        setValue("");
      }
    };
    $[44] = setValue;
    $[45] = value;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== setValue) {
    t24 = e_1 => setValue(e_1.target.value);
    $[47] = setValue;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  const t25 = getPlaceholder();
  let t26;
  if ($[49] !== isSearchPage || $[50] !== t23 || $[51] !== t24 || $[52] !== t25 || $[53] !== value) {
    t26 = <input type="text" value={value} onKeyDown={t23} onChange={t24} placeholder={t25} className="ml-2 h-full w-0 min-w-0 grow bg-transparent text-textPrimary outline-none placeholder:text-textSecondary" ref={searchBarInputRef} autoFocus={isSearchPage} />;
    $[49] = isSearchPage;
    $[50] = t23;
    $[51] = t24;
    $[52] = t25;
    $[53] = value;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== setValue || $[56] !== value.length) {
    t27 = value.length > 0 && <button type="button" onClick={() => {
      setValue("");
      searchBarInputRef.current?.focus();
    }} aria-label="Clear search" className="mr-1 shrink-0 rounded-full p-1 text-textSecondary transition-colors hover:text-textPrimary"><X height={16} width={16} /></button>;
    $[55] = setValue;
    $[56] = value.length;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t21 || $[59] !== t22 || $[60] !== t26 || $[61] !== t27) {
    t28 = <div className="ml-2 flex w-full min-w-0 items-center justify-between">{t21}{t22}{t26}{t27}</div>;
    $[58] = t21;
    $[59] = t22;
    $[60] = t26;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== isSearchPage || $[64] !== renderSuggestions) {
    t29 = renderSuggestions({
      fullWidth: isSearchPage
    });
    $[63] = isSearchPage;
    $[64] = renderSuggestions;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== content || $[67] !== handleSearchResultClick || $[68] !== isMapPage) {
    t30 = isMapPage && <SearchDropdown query={content} onResultClick={handleSearchResultClick} />;
    $[66] = content;
    $[67] = handleSearchResultClick;
    $[68] = isMapPage;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== searchHandler || $[71] !== t16 || $[72] !== t28 || $[73] !== t29 || $[74] !== t30) {
    t31 = <form data-id="search-bar" className={t16} onSubmit={searchHandler}>{t28}{t29}{t30}</form>;
    $[70] = searchHandler;
    $[71] = t16;
    $[72] = t28;
    $[73] = t29;
    $[74] = t30;
    $[75] = t31;
  } else {
    t31 = $[75];
  }
  return t31;
};
export default SearchBar;
function _temp(content_0) {
  const words = [];
  content_0.trim().split(/\s+/).forEach(word => {
    if (word && !(word.startsWith("@") || word.startsWith("s/") || word.startsWith("f/"))) {
      words.push(word);
    }
  });
  return words;
}